import React, { useState } from "react";
import { Phone, Mail, Building2, MapPin, Send, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const SUBURB_HINT = "";

export default function BlogInlineForm({
  testid = "blog-inline-form",
  variant = "compliance",
}) {
  const [form, setForm] = useState({
    business: "",
    name: "",
    phone: "",
    email: "",
    suburb: SUBURB_HINT,
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const [error, setError] = useState("");

  const copy =
    variant === "compliance"
      ? {
          overline: "/ Free on-site check",
          title: "Find out where you stand — in 30 minutes.",
          subtitle:
            "Honest assessment. Clear priorities. No scare tactics. Adelaide-wide. Zero callout fee.",
          service: "Cyber compliance check",
          defaultMessage:
            "I'd like a free 30-minute compliance check at my business.",
        }
      : {
          overline: "/ Free phone triage",
          title: "Tell us what's broken.",
          subtitle: "We'll get back within one business hour.",
          service: "Blog enquiry",
          defaultMessage: "",
        };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.phone) {
      setError("Name and phone are required.");
      return;
    }
    setStatus("submitting");
    try {
      const businessPrefix = form.business ? `[${form.business}] ` : "";
      const finalMessage =
        (form.message || copy.defaultMessage).trim() ||
        copy.defaultMessage;
      await api.post("/bookings", {
        name: `${businessPrefix}${form.name}`.trim(),
        phone: form.phone,
        email: form.email || null,
        suburb: form.suburb || SUBURB_HINT,
        service: copy.service,
        urgency: "standard",
        message: finalMessage,
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail ||
          "Couldn't send right now. Please call 1800 836 390 instead.",
      );
    }
  };

  if (status === "done") {
    return (
      <div
        className="not-prose my-12 te-card p-6 md:p-8 border-white/10"
        data-testid={`${testid}-success`}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-10 h-10 flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--te-primary)" }}
          >
            <Check size={20} color="#fff" />
          </div>
          <div>
            <div className="te-overline text-[10px] mb-2">/ We got it</div>
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-2 leading-snug">
              Thanks — we'll be in touch within one business hour.
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              In the meantime, feel free to call us directly on{" "}
              <strong className="text-white">1800 836 390</strong> if it's
              urgent.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="not-prose my-12 te-card p-6 md:p-8 border-white/10"
      data-testid={testid}
    >
      <div className="te-overline text-[10px] mb-2">{copy.overline}</div>
      <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 leading-snug">
        {copy.title}
      </h3>
      <p className="text-sm text-zinc-400 leading-relaxed mb-6">
        {copy.subtitle}
      </p>

      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
            <Building2 size={11} /> Business name (optional)
          </span>
          <input
            type="text"
            value={form.business}
            onChange={update("business")}
            data-testid={`${testid}-business`}
            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
            autoComplete="organization"
          />
        </label>

        <label className="block">
          <span className="te-overline text-[10px] block mb-1.5">
            Your name *
          </span>
          <input
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            data-testid={`${testid}-name`}
            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
            autoComplete="name"
          />
        </label>

        <label className="block">
          <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
            <Phone size={11} /> Phone *
          </span>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={update("phone")}
            data-testid={`${testid}-phone`}
            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
            autoComplete="tel"
          />
        </label>

        <label className="block">
          <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
            <Mail size={11} /> Email (optional)
          </span>
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            data-testid={`${testid}-email`}
            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
            autoComplete="email"
          />
        </label>

        <label className="block">
          <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
            <MapPin size={11} /> Suburb
          </span>
          <input
            type="text"
            value={form.suburb}
            onChange={update("suburb")}
            data-testid={`${testid}-suburb`}
            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
            autoComplete="address-level2"
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="te-overline text-[10px] block mb-1.5">
            Anything we should know? (optional)
          </span>
          <textarea
            value={form.message}
            onChange={update("message")}
            rows={3}
            data-testid={`${testid}-message`}
            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors resize-none"
          />
        </label>

        {error && (
          <div
            className="sm:col-span-2 text-sm px-3 py-2 border border-red-500/30 bg-red-500/10 text-red-300"
            data-testid={`${testid}-error`}
          >
            {error}
          </div>
        )}

        <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 mt-1">
          <button
            type="submit"
            disabled={status === "submitting"}
            data-testid={`${testid}-submit`}
            className="te-btn te-btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === "submitting" ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send size={16} /> Request my free check
              </>
            )}
          </button>
          <a
            href="tel:1800836390"
            className="te-btn te-btn-ghost justify-center"
            data-testid={`${testid}-call`}
          >
            <Phone size={16} /> Or call 1800 836 390
          </a>
        </div>
      </form>
    </div>
  );
}
